import React from "react";
import { useTranslation } from "react-i18next";
import { matchRoutes } from "react-router";
import { useFeatureFlag } from "@hooks";
import {
  SidebarMenu,
  MenuItem,
  MenuItemExpandable,
  FeatureFlagMenuItem,
  FeatureFlagMenuItemExpandable,
} from "@components";
import { MenuGroup } from "../SidebarMenu/MenuGroup";
import { usePathOrRouteMatcher } from "../../../../../hooks/usePathMatcher";
import { sidebarIcons } from "./icons";
import {
  CASHBACK_PATHS,
  CASHFLOW_ANALYZER_PATHS,
  DEPOTS_ACCOUNTS_ROUTES,
  GOALS_ROUTES,
  INVESTMENTS_SELECTED_ROUTES,
} from "./constants";

export interface AppSidebarMenuProps {
  className?: string;
  collapsed?: boolean;
}

const useShowSimplifiedInvestments = () => {
  const areWatchlistsEnabled = useFeatureFlag("WATCHLISTS");
  const isCashbackEnabled = useFeatureFlag("CLASSIFICATIONS");
  const isContractAnalyzerEnabled = useFeatureFlag("DIVIDEND_PLANNER");
  return (
    !areWatchlistsEnabled && !isCashbackEnabled && !isContractAnalyzerEnabled
  );
};

export const AppSidebarMenu = ({
  className,
  collapsed = false,
}: AppSidebarMenuProps) => {
  const { t } = useTranslation();
  const { matchesPath, matchesRoutes } = usePathOrRouteMatcher();
  const isSimplifiedInvestments = useShowSimplifiedInvestments();
  const isCashbackEnabled = useFeatureFlag("CASHBACK_WIZARD");
  const isContractAnalyzerEnabled = useFeatureFlag("CONTRACT_ANALYZER");

  const isCashflowAnalyzerSelected = matchesPath(CASHFLOW_ANALYZER_PATHS);
  const isInvestmentSelected = matchesRoutes(INVESTMENTS_SELECTED_ROUTES);
  const isDepotsAccountsSelected = matchesRoutes(DEPOTS_ACCOUNTS_ROUTES);
  const isGoalsSelected = matchesRoutes(GOALS_ROUTES);

  return (
    <SidebarMenu className={className} collapsed={collapsed}>
      <MenuGroup>
        <MenuItem
          to="/"
          name={t("appSidebar.menu.home")}
          before={sidebarIcons[matchesPath("/") ? "homeSelected" : "home"]}
          selected={matchesPath("/")}
        />
        {isSimplifiedInvestments ? (
          <MenuItem
            to="/investments"
            name={t("appSidebar.menu.investments")}
            before={
              sidebarIcons[
                isInvestmentSelected ? "investmentsSelected" : "investments"
              ]
            }
          />
        ) : (
          <MenuItemExpandable
            to="/investments"
            name={t("appSidebar.menu.investments")}
            before={
              sidebarIcons[
                isInvestmentSelected ? "investmentsSelected" : "investments"
              ]
            }
            selected={isInvestmentSelected}
          >
            <MenuItem
              selected={matchesPath("/investments")}
              to="/investments"
              name={t("appSidebar.menu.portfolio")}
            />
            <FeatureFlagMenuItem
              featureFlag="WATCHLISTS"
              selected={Boolean(
                matchRoutes([{ path: "/watchlists/*" }], location)
              )}
              to="/watchlists"
              name={t("appSidebar.menu.watchlists")}
            />
            <FeatureFlagMenuItem
              featureFlag="CLASSIFICATIONS"
              selected={matchesPath("/classifications")}
              to="/classifications"
              name={t("appSidebar.menu.classification")}
            />
            <FeatureFlagMenuItem
              featureFlag="DIVIDEND_PLANNER"
              selected={matchesPath("/dividend-planner")}
              to="/dividend-planner"
              name={t("appSidebar.menu.dividendPlanner")}
            />
          </MenuItemExpandable>
        )}
        {isCashbackEnabled && (
          <MenuItemExpandable
            to="/cashback"
            name={t("appSidebar.menu.cashback")}
            before={
              sidebarIcons[
                matchesPath(CASHBACK_PATHS) ? "cashbackSelected" : "cashback"
              ]
            }
            selected={matchesPath(CASHBACK_PATHS)}
          >
            <MenuItem
              to="/cashback"
              name={t("appSidebar.menu.overview")}
              selected={matchesPath("/cashback")}
            />
            <MenuItem
              to="/cashback-wizard-start"
              name={t("appSidebar.menu.newCashback")}
            />
            <MenuItem
              to="/cashback-import"
              name={t("appSidebar.menu.oneClickImport")}
              selected={matchesPath("/cashback-import")}
            />
          </MenuItemExpandable>
        )}

        <MenuItem
          to="/depots-accounts"
          name={t("appSidebar.menu.depotsAndAccounts")}
          before={
            sidebarIcons[
              isDepotsAccountsSelected ? "accountsSelected" : "accounts"
            ]
          }
          selected={isDepotsAccountsSelected}
        />

        {!isContractAnalyzerEnabled ? (
          <FeatureFlagMenuItem
            featureFlag="CASHFLOW_ANALYZER"
            to="/cashflow-analyzer"
            name={t("appSidebar.menu.cashflowAnalyzer")}
            selected={matchesPath("/cashflow-analyzer")}
            before={
              sidebarIcons[
                matchesPath("/cashflow-analyzer")
                  ? "cashflowAnalyzerSelected"
                  : "cashflowAnalyzer"
              ]
            }
          />
        ) : (
          <FeatureFlagMenuItemExpandable
            featureFlag="CASHFLOW_ANALYZER"
            to="/cashflow-analyzer"
            name={t("appSidebar.menu.analyse")}
            before={
              sidebarIcons[
                isCashflowAnalyzerSelected
                  ? "cashflowAnalyzerSelected"
                  : "cashflowAnalyzer"
              ]
            }
            selected={isCashflowAnalyzerSelected}
          >
            <FeatureFlagMenuItem
              to="/cashflow-analyzer"
              name={t("appSidebar.menu.cashflowAnalyzer")}
              selected={matchesPath("/cashflow-analyzer")}
              featureFlag="CASHFLOW_ANALYZER"
            />
            <FeatureFlagMenuItem
              featureFlag="CONTRACT_ANALYZER"
              to="/contract-analyzer"
              name={t("appSidebar.menu.contractAnalyzer")}
              selected={matchesPath("/contract-analyzer")}
            />
          </FeatureFlagMenuItemExpandable>
        )}
        <FeatureFlagMenuItem
          featureFlag="GOALS"
          to="/goals"
          name={t("appSidebar.menu.goals")}
          before={sidebarIcons[isGoalsSelected ? "goalsSelected" : "goals"]}
          selected={isGoalsSelected}
        />
      </MenuGroup>
    </SidebarMenu>
  );
};
